import { getAuditOperationsQueryKey, useAuditUndo } from "@api/generated/audit/audit";
import {
  devicesPlot,
  devicesReadings,
  getDevicesRecentQueryKey,
  useDevicesDevices,
  useDevicesRecent,
} from "@api/generated/devices/devices";
import { useGenebankContainers } from "@api/generated/genebank/genebank";
import { measurementsRows, useMeasurementsTables } from "@api/generated/measurements/measurements";
import type { PageOut, PlotCard, ReadingIn, ReadingResult, StationSettings, TableOut } from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { Icon } from "@app/icons";
import { ButtonLink } from "@app/links";
import { useTrial } from "@app/trials";
import { hasRole, useSaveSetting, useSeasonFromLink, useSettings } from "@app/workspace";
import { GenebankStation } from "@features/genebank/GenebankStation";
import { parseNumberText } from "@features/trials/clipboard";
import { rangeStats } from "@features/trials/range";
import { StationLog } from "@features/trials/StationLog";
import { type Live, LivePanel, PlotMap, PlotSummary } from "@features/trials/StationPanels";
import { isOnline, StationSetup } from "@features/trials/StationSetup";
import { ScanBox, useStationSend } from "@features/trials/StationShell";
import { expectedRange, fieldTargets, plotMap } from "@features/trials/station";
import { type Computed, compute, flourYield, MODE_TARGET, missingSettings, WEIGHT_MODES } from "@features/trials/stationModes";
import { TrialPicker, useTrialPlots } from "@features/trials/TrialPicker";
import "@features/trials/station.css";
import { notifications } from "@mantine/notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { cellText, formatNumber } from "@ui/format";
import { notifyError } from "@ui/notify";
import { PageHeader } from "@ui/PageHeader";
import { Splitter } from "@ui/Splitter";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const search = z.object({
  ev: z.number().int().optional(),
  kis: z.string().optional(),
  th: z.string().optional(),
});

export const Route = createFileRoute("/trials/data-input")({ validateSearch: search, component: MeasuringStation });

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};
const asNumber = (v: unknown): number | null => {
  const n = typeof v === "string" ? parseNumberText(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
};
// the milling columns a flour-yield mode shows for the scanned plot
const MILLING = ["buza_suly", "minus_zel", "liszt_suly", "lisztkihozatal"];

/** Kísérletek → Adatfelvétel, the measuring station: scan a plot's barcode, see its line, its repetitions and last
 *  year's value, then record the instrument's live value (Enter) or type one. Replaces the legacy per-instrument
 *  screens (frmMettler with its weight types, frmMinolta, frmEses, frmInframatic); the instruments' own clients send
 *  through /api/v1/eszkoz. */
function MeasuringStation() {
  const { t } = useTranslation();
  const settings = useSettings();
  const roles = useUserMe().data?.roles;
  const canRecord = hasRole(roles, "nemesito");
  const tables = useMeasurementsTables({ query: { staleTime: Number.POSITIVE_INFINITY } }).data;
  if (!canRecord || !settings || !tables) {
    return (
      <div className="page">
        <PageHeader title={t("dataInput.title")} />
        {settings && !canRecord && <p className="mu">{t("dataInput.breederOnly")}</p>}
      </div>
    );
  }
  return <Station station={settings.station} tables={tables} canWeighGenebank={hasRole(roles, "genbank")} />;
}

function Station({ station, tables, canWeighGenebank }: { station: StationSettings; tables: TableOut[]; canWeighGenebank: boolean }) {
  const { t } = useTranslation();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const client = useQueryClient();
  const saveSetting = useSaveSetting();
  const ev = useSeasonFromLink(params.ev);
  const { trials, trial, th } = useTrial(ev, params.kis, params.th);
  const kis = trial?.kis;
  const mode = station.mode === "genebank" && !canWeighGenebank ? "values" : station.mode;
  const fixed = MODE_TARGET[mode];
  const table = tables.find((x) => x.name === (fixed?.table ?? station.table));
  const targets = fieldTargets(table);
  const target = fixed ? targets.find((f) => f.value === fixed.field) : (targets.find((f) => f.value === station.field) ?? targets[0]);
  const columnOf = (name: string | null) => table?.columns.find((c) => c.name === name);
  const labelOf = (parc: number, column: string | null) => `${parc} · ${columnOf(column)?.label ?? column ?? ""}`;
  const missing = missingSettings({ ...station, mode });

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10_000);
    return () => clearInterval(id);
  }, []);
  // the chosen instrument's live value every second; without one, the list only for the online dots
  const devices = useDevicesDevices({ query: { refetchInterval: station.device ? 1000 : 15_000 } }).data ?? [];
  const device = devices.find((d) => d.client_id === station.device);
  const live = device && isOnline(device, now) ? (device.live as Live | null) : null;
  const containers = useGenebankContainers({ query: { enabled: mode === "genebank", staleTime: 60_000 } }).data ?? [];

  const plots = useTrialPlots(ev, kis);
  const rowsKey = ["station-rows", ev, table?.name, th, kis] as const;
  const rows =
    useQuery({
      queryKey: rowsKey,
      enabled: !!ev && !!table && !!kis && mode !== "genebank",
      queryFn: () => measurementsRows(ev ?? 0, table?.name ?? "", { th, kis, limit: 5000 }),
    }).data?.rows ?? [];
  const [since] = useState(startOfToday);
  const log = useDevicesRecent({ since }).data ?? [];

  // a card was read for one mode, table and field (its column): another setup scans again. recorded: read again after
  // our own reading (so its value is no "second scan"); moisture: the plot's FOSS grain moisture (grain conditioning)
  const setup = `${mode}|${table?.name}|${target?.value}`;
  const [card, setCard] = useState<{ setup: string; code: string; plot: PlotCard; recorded: boolean; moisture: number | null } | null>(
    null,
  );
  const current = card?.setup === setup ? card : null;
  const [kernels, setKernels] = useState<number | string>("");
  const plot = current?.plot;
  const column = plot?.column ?? null;

  const range = useMemo(
    () => (target ? expectedRange(rows.flatMap((r) => target.columns.flatMap((c) => asNumber(r[c]) ?? []))) : null),
    [rows, target],
  );
  const tiles = useMemo(() => {
    if (mode === "grain" || mode === "conditioning" || mode === "flour") return MILLING;
    // the repetitions the trial uses, and the one the reading goes to
    return (target?.columns ?? []).filter((c) => c === column || !target?.group || rows.some((r) => r[c] != null));
  }, [mode, target, column, rows]);
  const mapColumn = column ?? target?.columns[0] ?? null;
  const cells = useMemo(
    () =>
      plotMap(
        plots.map((r) => r.parc as number),
        rows.filter((r) => (r.alp ?? 0) === (plot?.alp ?? 0)),
        mapColumn,
        station.route_width,
        plot?.parc,
      ),
    [plots, rows, plot, mapColumn, station.route_width],
  );
  const doneCount = cells.filter((c) => c.state === "done").length;
  const liveNumber = asNumber(live?.value);
  const kernelCount = typeof kernels === "number" ? kernels : null;
  const context = { kernels: kernelCount, moisture: current?.moisture ?? station.fallback_moisture };
  const settings = { ...station, mode };
  const computed = liveNumber !== null ? compute(settings, liveNumber, context) : null;
  const previous = target && plot?.previous_values ? rangeStats(target.columns.map((c) => asNumber(plot.previous_values?.[c]))).mean : null;
  // a second scan of a label whose repetition is recorded already
  const repeatedValue = plot && !current?.recorded && (plot.repetition ?? 0) > 0 && column ? plot.values?.[column] : null;

  const saveStation = (patch: Partial<StationSettings>) => saveSetting("station", { ...station, ...patch });

  const lookup = async (text: string, recorded = false) => {
    if (!ev) return false;
    try {
      const ask = { code: text, ev, hungarian_keyboard: station.hungarian_keyboard, alp_mode: station.alp_mode };
      // grain conditioning reads the plot's FOSS grain moisture too
      const [found, foss] = await Promise.all([
        devicesPlot({ ...ask, table: table?.name, field: target?.value }),
        mode === "conditioning" ? devicesPlot({ ...ask, table: "foss" }) : null,
      ]);
      setCard({ setup, code: text, plot: found, recorded, moisture: asNumber(foss?.values?.foss_nedv) });
      setKernels("");
      if (found.kis !== kis || (params.th && found.th !== params.th)) navigate({ search: (s) => ({ ...s, kis: found.kis, th: found.th }) });
      return true;
    } catch (e) {
      notifyError(e);
      return false;
    }
  };

  /** The stored values into the cached rows and the card: no refetch of the trial per reading. */
  const remember = (reading: ReadingIn) => {
    client.setQueryData<PageOut>(rowsKey, (page) => {
      if (!page) return page;
      const at = page.rows.findIndex((r) => r.parc === reading.parc && (r.alp ?? 0) === reading.alp);
      if (at === -1) {
        void client.invalidateQueries({ queryKey: rowsKey }); // a new row: fetch it
        return page;
      }
      return { ...page, rows: page.rows.map((r, i) => (i === at ? { ...r, ...reading.values } : r)) };
    });
    if (current) void lookup(current.code, true); // the next repetition, from the server's rule (and the derived values)
    void client.invalidateQueries({ queryKey: getDevicesRecentQueryKey({ since }) });
    void client.invalidateQueries({ queryKey: ["measurements"] }); // the Kísérleti adatok grid
  };

  const sentValue = (reading: ReadingIn) => Object.values(reading.values)[0];
  const readingLabel = (reading: ReadingIn) => labelOf(reading.parc, Object.keys(reading.values)[0] ?? null);
  const { scanRef, busy, record, dialogs } = useStationSend<ReadingIn, ReadingResult>({
    conflict: station.conflict,
    post: async (reading, conflict) => (await devicesReadings({ conflict, readings: [reading] }))[0],
    onStored: (reading, result) => {
      notifications.show({
        color: result.status === "stored" ? "green" : "blue",
        message: t(`dataInput.status.${result.status}`, { plot: readingLabel(reading) }),
      });
      if (result.message === "not_in_register") notifications.show({ color: "yellow", message: t("dataInput.notInRegisterWarning") });
      setKernels("");
      remember(reading);
    },
    onRefused: (reading, result) =>
      notifications.show({
        color: "red",
        message: t(`dataInput.status.${result.status}`, { plot: readingLabel(reading) }),
      }),
    valueText: (reading) => cellText(sentValue(reading)),
    overwriteText: ({ item, result }) =>
      Object.entries(result.existing ?? {}).map(([c, old]) =>
        t("dataInput.overwriteText", { parc: labelOf(item.parc, c), oldValue: cellText(old), newValue: cellText(sentValue(item)) }),
      ),
  });

  const recordValue = (value: number | null, raw: number | null) => {
    if (!ev || !table || !plot || !column || value === null || missing.length) return;
    // flour yield: the Zeleny sample taken out goes with the flour, so the server's yield can be recomputed
    const zeleny = mode === "flour" && station.minus_zel != null ? { minus_zel: station.minus_zel } : {};
    const reading: ReadingIn = {
      key: crypto.randomUUID(),
      ev,
      th: plot.th,
      kis: plot.kis,
      parc: plot.parc,
      alp: plot.alp,
      table: table.name,
      values: { [column]: value, ...zeleny },
      measured_at: new Date().toISOString(),
    };
    record(reading, raw, value);
  };

  const undo = useAuditUndo({
    mutation: {
      onSuccess: () => {
        void client.invalidateQueries({ queryKey: rowsKey });
        void client.invalidateQueries({ queryKey: getDevicesRecentQueryKey({ since }) });
        void client.invalidateQueries({ queryKey: getAuditOperationsQueryKey() });
        if (current) void lookup(current.code);
      },
    },
  });

  const repLabel = columnOf(mapColumn)?.label;
  return (
    <div className="page fill">
      <PageHeader
        title={t("dataInput.title")}
        season
        subtitle={t("dataInput.subtitle")}
        item={kis && ev ? { kind: "trial", title: `${kis} · ${t("dataInput.title")}`, subtitle: String(ev) } : undefined}
        actions={
          <div className="hstack" style={{ gap: 8 }}>
            {cells.length > 0 && mode !== "genebank" && (
              <span className="chip g">{t("dataInput.progress", { done: doneCount, total: cells.length })}</span>
            )}
            {/* the same trial elsewhere: other pages, so links, not tabs */}
            <ButtonLink
              to="/trials/data"
              search={{ kis, th, table: table?.name ?? "trms" }}
              size="xs"
              variant="default"
              rightSection={<Icon name="arrowRight" size={12} />}
            >
              {t("dataInput.tabs.table")}
            </ButtonLink>
            <ButtonLink to="/field" search={{ kis, th }} size="xs" variant="default" leftSection={<Icon name="phone" size={14} />}>
              {t("dataInput.tabs.fieldMode")}
            </ButtonLink>
          </div>
        }
      />

      <div className="hstack grow" style={{ gap: 16, alignItems: "stretch", minHeight: 0 }}>
        <StationSetup
          trialPicker={
            <TrialPicker
              trials={trials}
              trial={trial}
              th={th}
              onChange={(v, site) => navigate({ search: (s) => ({ ...s, kis: v, th: site }) })}
            />
          }
          tables={tables}
          targets={targets}
          station={settings}
          onChange={saveStation}
          devices={devices}
          now={now}
          canWeighGenebank={canWeighGenebank}
          containers={containers.map((c) => c.name)}
          season={ev}
        />
        <Splitter cssVar="--station-setup-w" side="start" min={220} max={520} />

        <div className="stack grow" style={{ gap: 16, minWidth: 0, overflowY: "auto" }}>
          {mode === "genebank" && ev ? (
            <GenebankStation
              station={settings}
              ev={station.genebank_ev ?? ev}
              device={device ? (device.name ?? device.client_id) : null}
              live={live}
              liveNumber={liveNumber}
              onLogged={() => void client.invalidateQueries({ queryKey: getDevicesRecentQueryKey({ since }) })}
            />
          ) : (
            <>
              <ScanBox
                scanRef={scanRef}
                label={t("dataInput.scanPlaceholder")}
                onLookup={lookup}
                onRecord={() => recordValue(computed?.value ?? null, liveNumber)}
              >
                {missing.length > 0 && (
                  <p className="note warn sm">
                    {t("dataInput.missingSettings", { settings: missing.map((m) => t(`dataInput.setting.${m}`)).join(", ") })}
                  </p>
                )}
                {plot && (
                  <div className="hstack sm" style={{ gap: 6, flexWrap: "wrap" }}>
                    <span className="mu">{t("dataInput.parsedAs")}</span>
                    <span className="chip b">{t("dataInput.parsedSite", { th: plot.th })}</span>
                    <span className="chip b">{t("dataInput.parsedTrial", { kis: plot.kis })}</span>
                    <span className="chip b">{t("dataInput.parsedPlot", { parc: plot.parc })}</span>
                    {(plot.repetition ?? 0) > 0 && (
                      <span className="chip b">{t("dataInput.parsedRepetition", { alp: columnOf(column)?.label ?? plot.repetition })}</span>
                    )}
                    {plot.alp > 0 && <span className="chip b">{t("dataInput.parsedSubplot", { alp: plot.alp })}</span>}
                    {station.hungarian_keyboard && <span className="mu">{t("dataInput.keyboardFixed")}</span>}
                  </div>
                )}
              </ScanBox>

              {plot && (
                <section className="card pad" style={{ flexDirection: "row", flexWrap: "wrap", gap: 24, alignItems: "stretch" }}>
                  <PlotSummary plot={plot} tiles={tiles} columnOf={columnOf} previous={previous} />
                  <LivePanel
                    device={device ? (device.name ?? device.client_id) : null}
                    live={live}
                    liveNumber={liveNumber}
                    compute={(raw) => compute(settings, raw, context).value}
                    kernels={kernels}
                    onKernels={mode === "tkw" ? setKernels : undefined}
                    extra={
                      <ModeResult
                        station={settings}
                        plot={plot}
                        moisture={context.moisture}
                        foss={current?.moisture != null}
                        computed={computed}
                      />
                    }
                    manualLabel={mode === "tkw" ? t("dataInput.manualSample") : undefined}
                    range={range}
                    canRecord={!!column && missing.length === 0}
                    busy={busy}
                    onRecord={recordValue}
                  />
                </section>
              )}

              {cells.length > 0 && (
                <PlotMap
                  cells={cells}
                  width={station.route_width}
                  title={target?.group && repLabel ? t("dataInput.mapRepetition", { rep: repLabel }) : t("dataInput.map")}
                />
              )}
            </>
          )}
        </div>

        <Splitter cssVar="--station-log-w" side="end" min={200} max={520} />
        <StationLog
          log={log}
          tables={tables}
          undoing={undo.isPending ? (undo.variables?.opId ?? null) : null}
          onUndo={(opId) => undo.mutate({ opId })}
        >
          {mode !== "genebank" && plot && column && repeatedValue != null && (
            <div className="note warn">
              <b>{t("dataInput.rescan")}</b>
              <br />
              {t("dataInput.rescanText", { parc: plot.parc, rep: columnOf(column)?.label ?? "", value: cellText(repeatedValue) })}
            </div>
          )}
          {mode !== "genebank" && plot && target?.group && !column && <div className="note warn">{t("dataInput.allDone")}</div>}
        </StationLog>
      </div>

      {dialogs}
    </div>
  );
}

/** What the calculating modes show beside the live value: the water to add (written on the sample by hand) and the
 *  moisture it is from, the hectolitre weight, the flour yield the server will derive. */
function ModeResult(props: {
  station: StationSettings;
  plot: PlotCard;
  moisture: number | null; // the grain's moisture the water is computed from
  foss: boolean; // it is the plot's FOSS grain moisture (else the setup's moisture for this case)
  computed: Computed | null;
}) {
  const { t } = useTranslation();
  const { station, plot, moisture, computed } = props;
  const mode = station.mode;
  const grams = station.grams && WEIGHT_MODES.has(mode) && <span className="sm mu">{t("dataInput.gramsActive")}</span>;
  if (mode === "conditioning") {
    return (
      <>
        <span className="sm">
          {moisture == null
            ? t("dataInput.moistureMissing")
            : t(props.foss ? "dataInput.moistureFoss" : "dataInput.moistureFallback", { value: formatNumber(moisture, 1) })}
        </span>
        {computed?.water != null && (
          <div className="stack" style={{ gap: 0 }}>
            <span className="sec">{t("dataInput.waterToAdd")}</span>
            <span className="water">{t("dataInput.waterMl", { ml: formatNumber(computed.water * 1000, 0) })}</span>
            <span className="sm">{t("dataInput.wettedGrain", { kg: formatNumber(computed.value, 4) })}</span>
          </div>
        )}
        {grams}
      </>
    );
  }
  if (mode === "hl" && computed?.value != null)
    return <span className="sm">{t("dataInput.hlValue", { value: formatNumber(computed.value, 2) })}</span>;
  if (mode === "flour" && computed?.value != null) {
    const grain = asNumber(plot.values?.buza_suly);
    const yieldPct = flourYield(computed.value, grain, station.minus_zel ?? asNumber(plot.values?.minus_zel));
    return (
      <>
        <span className="sm">
          {yieldPct != null ? t("dataInput.flourYield", { value: formatNumber(yieldPct, 1) }) : t("dataInput.flourNoGrain")}
        </span>
        {grams}
      </>
    );
  }
  return grams || null;
}
